import { useLocalStorage, useStorage } from "@vueuse/core";
import { computed, ref } from "vue";

export interface AppConfig {
  apiUrl: string
  mercureUrl: string
  loginUrl: string
  loginRedirect: string
  loginClientId: string
  cdnUrl: string
  appEnv: string
  presetName?: string | null
  webUrl?: string | null
  authSidebarUrl?: string | null
  /** Base URL of the developer portal (`developer.verbleif.*`), which serves the API reference and the guides. */
  developerUrl?: string | null
  skipOauth?: boolean
}

const DEVELOPER_MODE_STORAGE_KEY = "developerMode";
const LEGACY_DEBUG_MODE_STORAGE_KEY = "debugMode";

function migrateDeveloperModeStorage(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const legacy = localStorage.getItem(LEGACY_DEBUG_MODE_STORAGE_KEY);
  if (legacy === null) {
    return;
  }
  if (localStorage.getItem(DEVELOPER_MODE_STORAGE_KEY) === null) {
    localStorage.setItem(DEVELOPER_MODE_STORAGE_KEY, legacy);
  }
  localStorage.removeItem(LEGACY_DEBUG_MODE_STORAGE_KEY);
}

function createConfigStore() {
  migrateDeveloperModeStorage();
  const initialConfig = ref<AppConfig | null>(null);
  const developerMode = useStorage<boolean>(DEVELOPER_MODE_STORAGE_KEY, false);

  // A serializer is required since the built-in serializer determines the type on the initial value.
  const overrideConfig = useLocalStorage<AppConfig | null>(
    "overrideConfig",
    null,
    {
      serializer: {
        read: (v: any) => (v ? JSON.parse(v) : null),
        write: (v: any) => JSON.stringify(v),
      },
    },
  );

  function setInitialConfig(newConfig: AppConfig) {
    initialConfig.value = newConfig;
  }

  function setOverrideConfig(newConfig: AppConfig) {
    overrideConfig.value = newConfig;
  }

  function reset() {
    overrideConfig.value = null;
  }

  const config = computed<AppConfig | null>(() => {
    if (overrideConfig.value) {
      return overrideConfig.value;
    }

    return initialConfig.value;
  });

  return {
    config,
    overrideConfig,
    setOverrideConfig,
    developerMode,
    setInitialConfig,
    reset,
  };
}

type ConfigStoreInterface = ReturnType<typeof createConfigStore>;
let backwardsCompatibleInstance: null | ConfigStoreInterface = null;
export function useConfigStore(): ConfigStoreInterface {
  if (backwardsCompatibleInstance === null) {
    backwardsCompatibleInstance = createConfigStore();
  }

  return backwardsCompatibleInstance;
}
export default useConfigStore;
